import React from 'react';
import { shouldShowByValue } from 'services/templateServices';
import { getRoleType } from 'services/TenantSubmitHandler';
import { TenantPetsData, TenantPetsDocument } from 'store/tenant/types';
import Button from '../components/UI/Button';
import Checkbox from '../components/UI/Checkbox';
import CheckboxGroup from '../components/UI/CheckboxGroup';
import InputDate from '../components/UI/InputDate';
import InputField from '../components/UI/InputField';
import RadioInput from '../components/UI/RadioInput/Radio';
import componentMap from './componentMap';
import { sanitizeForm } from './htmlSanitizer';
import { isQldRegion } from './petFormHelpers';
import { mapValidations } from './validationMap';

/**
 * Renders a list of template objects into React elements based on their type.
 *
 * This function supports various template types, including HTML, text, textarea, radio inputs,
 * custom components, and nested div structures. It uses a stack to manage nested divs and
 * supports skipping certain template types. Custom components are mapped via `componentMap`.
 *
 * @param templates - An array of template objects describing the UI elements to render.
 * @param handlers - An object containing handler functions, such as `register` for form fields and custom event handlers.
 * @param animalIndex - (Optional) The index of the animal, used for field naming in forms.
 * @param animalType - (Optional) The type of animal, used for field naming in forms.
 * @returns An array of React elements rendered according to the template definitions.
 */
export const renderTemplates = (
    templates: any[] = [],
    handlers: { [key: string]: Function },
    animalIndex?: number,
    animalType?: string,
    stageOnePetsData?: TenantPetsData[],
    stageOnePetsDocumentData?: TenantPetsDocument[],
    roleType?: string,
    petPosition?: number
) => {
    const stack: any[] = [];
    let result: any[] = [];

    // List all refusal reason checkbox field names (NSW and QLD)
    const refusalReasonNames = [
    // NSW reasons
    "unreasonable_number_of_animala",
    "fencing",
    "open_space",
    "welfare",
    "damage",
    "other_laws",
    "landlord_lives_at_property",
    "tenant_did_not_agree",
    // QLD reasons
    "qld_unreasonable_number",
    "qld_unsuitable_premises",
    "qld_likely_damage",
    "qld_health_safety_risk",
    "qld_contravene_law",
    "qld_body_corporate_bylaw",
    "qld_tenant_not_agreed_conditions",
    "qld_not_a_pet",
    "qld_licence_condition",
    "qld_another_ground"
    ];

    // QLD refusal reasons used for conditional explanation rendering
    const qldRefusalReasonNames = [
    "qld_unreasonable_number",
    "qld_unsuitable_premises",
    "qld_likely_damage",
    "qld_health_safety_risk",
    "qld_contravene_law",
    "qld_body_corporate_bylaw",
    "qld_tenant_not_agreed_conditions",
    "qld_not_a_pet",
    "qld_licence_condition",
    "qld_another_ground"
    ];

    // List all consent condition checkbox field names
    const conditionCheckboxNames = [
    "clause_57",
    "clause_58",
    "clause_59",
    "other_reasonable_conditions"
    ];

    const safeTemplates = Array.isArray(templates) ? templates : [];
    safeTemplates.forEach((template, index) => {

        // Skip rendering for certain roles
        const allowedRoles = template?.notForRole;

        if(allowedRoles && allowedRoles.length > 0) {
            const roleName = getRoleType()
            if ((!roleName || allowedRoles.includes(roleName)) && roleName !== "") {
                return; // Skip rendering if roleType does not match
            }
        }

        

        if(template?.type === "animal-cards-profile" || template?.type === "dynamic_tenant_form" || template?.type === "signature_of_tenants" || template?.type === "info-section" || template?.type === "animal-card-placeholder") {
            return; // Skip rendering for 'animal-cards-profile' type
        }

        // Animal type filtering logic
        // Example: Only render if the template's animal_type includes the current animalType
        if (animalType && Array.isArray(template.animal_type) && template.animal_type.length > 0) {
            if (!template.animal_type.includes(animalType)) {
                return; // Skip rendering if animalType does not match
            }
        }

        // Conditional rendering based on showIf property
        // Example: If one field depends on another field's value
        if (template.showIf) {
            // Build dynamic field name for dependency
            const depFieldName = animalType ? `${animalType}.${template.showIf.field}.${animalIndex}` : template.showIf.field;

            // For QLD refusal reasons explanation fields, show when any QLD refusal reason is selected.
            const isQldRefusalExplanationField =
                template.showIf.field === "qld_another_ground" && template.showIf.value === true;

            if (isQldRefusalExplanationField) {
                const qldReasonFieldNames = qldRefusalReasonNames.map((name) =>
                    animalType ? `${animalType}.${name}.${animalIndex}` : name
                );
                const hasAnyQldReasonSelected = handlers.watch
                    ? qldReasonFieldNames.some((name) => Boolean(handlers.watch(name)))
                    : false;

                if (!hasAnyQldReasonSelected) {
                    return; // Skip rendering if no QLD refusal reason is selected
                }
            } else {
                const depValue = handlers.watch ? handlers.watch(depFieldName) : undefined;
                if (depValue !== template.showIf.value) {
                    return; // Skip rendering if condition not met
                }
            }
        }

        // hideIf: hide this field when the referenced field matches the given value
        // Uses Boolean coercion so undefined is treated as false (fields show by default)
        if (template.hideIf) {
            const depFieldName = animalType
                ? `${animalType}.${template.hideIf.field}.${animalIndex}`
                : template.hideIf.field;
            const depValue = handlers.watch ? handlers.watch(depFieldName) : undefined;
            if (Boolean(depValue) === Boolean(template.hideIf.value)) {
                return; // Skip rendering — field is hidden
            }
        }

        if (!shouldShowByValue(template, handlers, animalType, animalIndex, stageOnePetsData)) {
            return;
        }


        if (template.type === 'open-div') {
            stack.push({
                type: 'div',
                props: { key: index, className: template.className },
                children: [],
            });
        } else if (template.type === 'close-div') {
            const closedDiv = stack.pop();
            const element = React.createElement('div', closedDiv?.props, ...closedDiv?.children);
            if (stack.length > 0) {
                stack[stack.length - 1].children.push(element);
            } else {
                result.push(element);
            }
        } else {
            let element = null;
            if (template.type === 'html') {
                element = (
                    <div key={index} dangerouslySetInnerHTML={{ __html: sanitizeForm(template.label) }} className={template.className} />
                );
            } else if (template.type === 'text' || template.type === 'textarea') {
                const fieldName = animalType ? `${animalType}.${template.name}.${animalIndex}` : template.name;
                // Remove key from the spread and pass it directly
                const { key, ...templateWithoutKey } = template;
                // Filter stageOnePetsData by animalType and animalIndex

                const atLeastOneOf = template?.validations?.[0]?.atLeastOneOf;
                const atLeastOneOfMessage = template?.validations?.[0]?.atLeastOneOfMessage;
                const otherFieldName = atLeastOneOf ? (animalType ? `${animalType}.${atLeastOneOf}.${animalIndex}` : atLeastOneOf) : null;

                const textareaValidation = mapValidations(
                    template?.validations,
                    typeof handlers.getValues === 'function'
                        ? (handlers.getValues as import('react-hook-form').UseFormGetValues<any>)
                        : undefined,
                    atLeastOneOf ? otherFieldName : "",
                    atLeastOneOfMessage
                );
                // When a textarea is conditionally shown based on a boolean checkbox (showIf.value === true),
                // unregister it when hidden so its required validation doesn't block form submission.
                if (template.showIf?.value === true) {
                    (textareaValidation as any).shouldUnregister = true;
                }

                element = (
                    <InputField
                        key={index}
                        {...templateWithoutKey}
                        as={template.type}
                        {...(typeof handlers.register === 'function' && template && fieldName
                            ? handlers.register(fieldName, textareaValidation)
                            : {})}
                        animalIndex={animalIndex}
                        animalType={animalType}
                        name={fieldName}
                        validations={template?.validations?.[0]}
                        disabled={roleType === "agent" ? true : template.disabled}
                        defaultValue={getDefaultValue(fieldName, stageOnePetsData ?? [], roleType)}
                        clearOnFill={template.clearOnFill}
                        atLeastOneOf={atLeastOneOf}
                    />
                );
            } else if (template.type === 'date') {
                const fieldName = animalType ? `${animalType}.${template.name}.${animalIndex}` : template.name;
                const { key, ...templateWithoutKey } = template;

                const atLeastOneOf = template?.validations?.[0]?.atLeastOneOf;
                const atLeastOneOfMessage = template?.validations?.[0]?.atLeastOneOfMessage;
                const otherFieldName = atLeastOneOf ? (animalType ? `${animalType}.${atLeastOneOf}.${animalIndex}` : atLeastOneOf) : null;

                element = (
                    <InputDate
                        key={index}
                        {...templateWithoutKey}
                        {...(typeof handlers.register === 'function' && template && fieldName
                            ? handlers.register(
                                fieldName,
                                mapValidations(
                                    template?.validations,
                                    typeof handlers.getValues === 'function'
                                        ? (handlers.getValues as import('react-hook-form').UseFormGetValues<any>)
                                        : undefined,
                                    atLeastOneOf ? otherFieldName : "",
                                    atLeastOneOfMessage
                                )
                                )
                            : {})}
                        animalIndex={animalIndex}
                        animalType={animalType}
                        name={fieldName}
                        validations={template?.validations?.[0]}
                        disabled={roleType === "agent" ? true : template.disabled}
                        defaultValue={getDefaultValue(fieldName, stageOnePetsData ?? [], roleType)}
                        clearOnFill={template.clearOnFill}
                        atLeastOneOf={atLeastOneOf}
                        min={template.min}
                        max={template.max}
                    />
                );
            } else if (template.type === 'checkbox') {
               const fieldName = animalType ? `${animalType}.${template.name}.${animalIndex}` : template.name; 
               const onChange = typeof template.onChange === 'string' && handlers[template.onChange]
                        ? handlers[template.onChange]
                        : () => {};

                const atLeastOneOf = template?.validations?.[0]?.atLeastOneOf;
                const atLeastOneOfMessage = template?.validations?.[0]?.atLeastOneOfMessage;
                const otherFieldName = atLeastOneOf ? (animalType ? `${animalType}.${atLeastOneOf}.${animalIndex}` : atLeastOneOf) : null;
                const getValues = typeof handlers.getValues === 'function' ? (handlers.getValues as import('react-hook-form').UseFormGetValues<any>) : undefined;
                
                // Check if this checkbox is a refusal reason
                const isRefusalReason = refusalReasonNames.includes(template.name);

                // Check if this checkbox is a consent condition
                const isConditionCheckbox = conditionCheckboxNames.includes(template.name);

                const groupFieldNames = isRefusalReason
                ? refusalReasonNames.map(name =>
                    animalType ? `${animalType}.${name}.${animalIndex}` : name
                )
                : [];

                const conditionFieldNames = isConditionCheckbox
                ? conditionCheckboxNames.map(name =>
                    animalType ? `${animalType}.${name}.${animalIndex}` : name
                )
                : [];



                // Extract validation logic into independent variable
                let checkboxValidation;
                if (isRefusalReason) {
                    checkboxValidation = {
                        validate: (value: any) => {
                            // Group validation: at least one refusal reason checked
                            const values = groupFieldNames.map(n => handlers.getValues(n));
                            if (!values.some(Boolean)) {
                                return "At least one refusal reason must be selected.";
                            }
                            // If this checkbox also has atLeastOneOf logic, check that too
                            if (atLeastOneOf && getValues) {
                                const otherValue = getValues(otherFieldName);
                                if (!value && !otherValue) {
                                    return atLeastOneOfMessage || "At least one field is required";
                                }
                            }
                            return true;
                        }
                    };
                } else if (isConditionCheckbox) {
                    checkboxValidation = {
                        validate: (value: any) => {
                            // Check if conditions are required (i.e., "There are conditions" is selected)
                            const consentConditionsFieldName = animalType ? `${animalType}.agent_consent_conditions.${animalIndex}` : 'agent_consent_conditions';
                            const consentConditionsValue = handlers.getValues ? handlers.getValues(consentConditionsFieldName) : undefined;

                            // Only validate if "i_consent_conditions_apply" is selected
                            if (consentConditionsValue === "i_consent_conditions_apply") {
                                // Group validation: at least one condition checkbox must be checked
                                const values = conditionFieldNames.map(n => handlers.getValues(n));
                                if (!values.some(Boolean)) {
                                    return "At least one condition must be selected.";
                                }
                            }

                            // If this checkbox also has atLeastOneOf logic, check that too
                            if (atLeastOneOf && getValues) {
                                const otherValue = getValues(otherFieldName);
                                if (!value && !otherValue) {
                                    return atLeastOneOfMessage || "At least one field is required";
                                }
                            }
                            return true;
                        }
                    };
                } else {
                    checkboxValidation = mapValidations(
                        template?.validations,
                        getValues,
                        atLeastOneOf ? otherFieldName : "",
                        atLeastOneOfMessage
                    );
                }

                // Extract register logic into independent variable
                let checkboxRegisterProps = {};
                if (handlers.register && template && fieldName) {
                    checkboxRegisterProps = handlers.register(fieldName, checkboxValidation);
                }

                element = (
                    <Checkbox
                        key={index}
                        {...template}
                        // options={template.options}
                        onChange={onChange}
                        {...checkboxRegisterProps}
                        name={fieldName}
                        disabled={roleType === "agent" ? true : template.disabled}
                        defaultState={template.defaultState || false}
                        defaultDisabled={template.defaultDisabled || false}
                        showValidationErrorMessage={template.showValidationErrorMessage ?? true}
                    />
                );
            } else if (template.type === 'checkboxGroup') {
                // Build options with proper names (respect animal scoping)
                const options = Array.isArray(template.options) ? template.options.map((opt: any) => {
                    const optName = animalType ? `${animalType}.${opt.name}.${animalIndex}` : opt.name;
                    return { ...opt, name: optName };
                }) : [];

                const groupName = animalType ? `${animalType}.${template.name}.${animalIndex}` : template.name;

                element = (
                    <CheckboxGroup
                        key={index}
                        name={groupName}
                        options={options}
                        className={template.className}
                        validationMessage={template?.validations?.[0]?.atLeastOneOfMessage}
                    />
                );
            } else if (template.type === 'radio') {
               const fieldName = animalType ? `${animalType}.${template.name}.${animalIndex}` : template.name;
               const onChange = typeof template.onChange === 'string' && handlers[template.onChange]
                        ? handlers[template.onChange]
                        : () => {};
                const onClick = template.onClickHandler && handlers[template.onClickHandler]
                                ? (event: React.MouseEvent<HTMLInputElement>) => handlers[template.onClickHandler](event)
                                : undefined;

                const atLeastOneOf = template?.validations?.[0]?.atLeastOneOf;

                // Calculate default value - prioritize form state over stale API data
                const getValues = typeof handlers.getValues === 'function' ? (handlers.getValues as import('react-hook-form').UseFormGetValues<any>) : undefined;

                // Priority order depends on role:
                // - For agent/landlord (readonly): Always use API data as source of truth
                // - For tenant (editable): 1. Current form value, 2. Template default, 3. API data
                const currentFormValue = getValues?.(fieldName);
                const formValueExists = currentFormValue !== undefined && currentFormValue !== null && currentFormValue !== "";

                let calculatedDefaultValue;
                if (roleType === "agent" || roleType === "landlord") {
                    // For agent/landlord readonly views, always use API data
                    calculatedDefaultValue = getDefaultRadioValue(fieldName, stageOnePetsData ?? []) || template?.defaultValue || "";
                } else if (formValueExists) {
                    // Use form value (non-empty)
                    calculatedDefaultValue = currentFormValue;
                } else if (template?.defaultValue) {
                    // Use template default if no form value exists
                    calculatedDefaultValue = template.defaultValue;
                } else {
                    // Only use API value if no form value exists at all
                    calculatedDefaultValue = getDefaultRadioValue(fieldName, stageOnePetsData ?? []);
                }
                
                
                element = (
                    <RadioInput
                        key={index}
                        {...template}
                        options={template.options}
                        onChange={onChange}
                        name={fieldName}
                        disabled={roleType === "agent" ? true : template.disabled}
                        clearOnFill={template.clearOnFill}
                        {...(calculatedDefaultValue && calculatedDefaultValue !== "" ? { defaultValue: calculatedDefaultValue } : {})}
                        onClick={onClick}
                        validations={template?.validations?.[0]}
                        required={template?.required}
                        atLeastOneOf={atLeastOneOf}
                    />
                );
            } else if (template?.type === "component") {
                const Comp = componentMap[template.component];
                const restProps = { key: index, ...template };
                const isSMSNotificationChecked = handlers.watch ? handlers.watch('sms_notification') : false;
                if(template?.component === "TenantForm" && template.tenantNumber) {
                    restProps.startIndex = template.tenantNumber;
                    restProps.maximumTenants = template.maximumTenants || 2;
                    if (handlers.register) {
                        restProps.register = handlers.register;
                    }
                    if(handlers.unregister) {
                        restProps.unregister = handlers.unregister;
                    }
                    restProps.smsNotification = isSMSNotificationChecked;
                }

                 if(template?.component === "UploadDocument"){
                    restProps.pet_type = animalType;
                    restProps.property_id = animalIndex;
                    restProps.pet_index = animalIndex;
                    restProps.petPosition = petPosition;
                    restProps.handleUploadPetDocuments = handlers.handleUploadPetDocuments;
                    restProps.petDocuments = stageOnePetsDocumentData || [];
                 }

                if(template?.component === "InputPhone"){
                    restProps.validations = mapValidations(template?.validations);
                    restProps.rules = mapValidations(template?.validations);
                    restProps.disableForRole = template.disableForRole || [];
                    restProps.requiredWhenCheckedField = template.requiredWhenCheckedField || "";
                    restProps.requiredWhenCheckedValue = template.requiredWhenCheckedValue;
                }

                if(template?.component === "AddressInput"){
                    restProps.rules = mapValidations(template?.validations);
                    restProps.name = template.name || '';
                }

                if(template?.component === "RangeSelector") {
                    const fieldName = animalType ? `${animalType}.${template.name}.${animalIndex}` : template.name;  
                    restProps.name = fieldName;
                    if (handlers.register) {
                        // Explicitly cast getValues to UseFormGetValues<any> if it's a function
                        const getValues = typeof handlers.getValues === 'function' ? (handlers.getValues as import('react-hook-form').UseFormGetValues<any>) : undefined;
                        restProps.register = (name: string) => handlers.register(name, mapValidations(template?.validations, getValues));
                        restProps.validations = mapValidations(template?.validations);
                        restProps.value = getDefaultRadioValue(fieldName, stageOnePetsData ?? []) || undefined
                    }
                
                }

                //added to handle onClick events for components like UploadDocument
                if (template.onClickHandler && handlers[template.onClickHandler]) {
                    restProps.onClick = () => handlers[template.onClickHandler](template.onClickArg);
                }
                
                // For tooltip or other components that may have HTML children
                if (template.childrenHtml) {
                    const { key, ...propsWithoutKey } = restProps;
                    element = Comp ? (
                        <Comp key={key ?? index} {...propsWithoutKey}>
                            <span dangerouslySetInnerHTML={{ __html: sanitizeForm(template.childrenHtml) }} />
                        </Comp>
                    ) : null;
                } else {
                    element = Comp ? React.createElement(Comp, { key: index, ...restProps }) : null;
                }
            } else if (template.type === 'button') {
                const onClick =
                    typeof template.onClick === 'string' && handlers[template.onClick]
                        ? (event: React.MouseEvent<HTMLButtonElement>) => handlers[template.onClick](event)
                        : () => {};
                element = (
                    <Button
                        key={index}
                        text={template.text}
                        iconName={template.iconName}
                        iconColor={template.iconColor}
                        disabled={template.disabled}
                        onClick={onClick}
                        className={template.className}
                        id={template.id}
                        type={template.type || 'button'}
                    />
                );
            }
            if (stack.length > 0) {
                stack[stack.length - 1].children.push(element);
            } else {
                result.push(element);
            }
        }
    });

    // If there are any unclosed divs, flush them to result
    while (stack.length > 0) {
        const closedDiv = stack.pop();
        const element = React.createElement('div', closedDiv.props, ...closedDiv.children);
        if (stack.length > 0) {
            stack[stack.length - 1].children.push(element);
        } else {
            result.push(element);
        }
    }

    return result;
};


/**
 * Filters and retrieves animal card profile templates for a specific animal type.
 *
 * @param jsonArray - An array of template objects to filter.
 * @param animalType - The type of animal to filter profiles for (e.g., "dog", "cat").
 * @returns An array of template objects that match the specified animal type and are of type "animal-cards-profile".
 *
 * @example
 * ```ts
 * const dogProfiles = getAnimalCardProfile(templates, "dog");
 * ```
 */
export function getAnimalCardProfile(jsonArray: any[], animalType: string): any[] {
    return jsonArray.filter(
        item => item.type === "animal-cards-profile"
        // item => item.type === "animal-cards-profile" && item.animal_type === animalType
    );
}

// Recursively collect 'name' props from children (all fields, used for payload building)
export const collectFieldNames = (children: React.ReactNode): string[] => {
  const names: string[] = [];

  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child)) {
      if (
        child.props &&
        typeof child.props === 'object' &&
        'name' in child.props
      ) {
        names.push((child.props as { name?: string }).name!);
      }
      // Recursively check child.props.children
      if (
        child.props &&
        typeof child.props === 'object' &&
        'children' in child.props
      ) {
        names.push(...collectFieldNames((child.props as { children?: React.ReactNode }).children));
      }
    }
  });

  return names;
}


export const getDefaultValue = (fieldName: string, tenantPetData: any[], roleType: string = "") => {
  // Example fieldName: "dog.microchipNumber.0"
  const [petType, fieldKey, indexStr] = fieldName.split(".");
  const index = Number(indexStr);

  // Map fieldKey to pet object property
  const fieldMap: Record<string, string> = {
    microchipNumber: "microchip_number",
    type_of_animal: "custom_type",
    reason_for_exemption: "microchip_exemption_reason",
    // Note: QLD fields (pet_sex, fur_colour_length, weight_height_length) don't need mapping
    // - API returns these with the same names as form fields
    // Dev Note: add other mappings as needed, e.g key is our field name and value is pet object property from api
  };

  const pet = tenantPetData
    .filter(p => p.pet_type === petType)
    [index];

  if (!pet) return (roleType === "agent" || roleType === "landlord") && isQldRegion() ? "N/A" : "";

  const petField = fieldMap[fieldKey] || fieldKey;
  const value = pet[petField] || "";
  // For QLD agent/landlord views, show "N/A" for empty non-applicable fields
  if (!value && (roleType === "agent" || roleType === "landlord") && isQldRegion()) return "N/A";
  return value;
}

export const getDefaultCheckboxValue = (fieldName: string, tenantPetData: any[]) => {
  const [petType, fieldKey, indexStr] = fieldName.split(".");
  const index = Number(indexStr);


  // Map fieldKey to pet object property
  const fieldMap: Record<string, string> = {
    "is_more_than_one": "has_multiple_animals",
    // add other mappings as needed
  };

  const pet = tenantPetData
    .filter(p => p.pet_type === petType)
    [index];

  if (!pet) return false;

  const petField = fieldMap[fieldKey] || fieldKey;
  return Boolean(pet[petField]);
}

export const getDefaultRadioValue = (fieldName: string, tenantPetData: any[]) => {
  if (!fieldName || !tenantPetData) return "";
  const [petType, fieldKey, indexStr] = fieldName.split(".");
  const index = Number(indexStr);
  const fieldMap: Record<string, string> = {
    microchipNumber: "microchip_number",
    microchipStatus: "microchip_status",
    // Note: pet_sex doesn't need mapping - API returns pet_sex directly
    // add other mappings as needed
  };
  const pet = tenantPetData.filter(p => p.pet_type === petType)[index];
  if (!pet) return "";
  const petField = fieldMap[fieldKey] || fieldKey;
  if (Array.isArray(pet[petField])) {
    return pet[petField].length > 0 ? pet[petField]?.[0] : "";
  }

  if(fieldKey === "choices" && typeof pet[petField] === "string" && pet[petField] !== ""){
    const arr = JSON.parse(pet[petField]);
    if (Array.isArray(arr)) {
      return arr[0]; // or whatever logic you need
    }
  }
  return pet[petField] || "";
}